import {
  TrendFrequency,
  Color,
} from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { Display } from '@fullstory/proto/fs/services/webber/frontend/display';
import {
  ScaleLinear,
  ScaleTime,
  curveLinear,
  extent,
  line,
  max,
  min,
  scaleLinear,
  scaleTime,
  ticks,
} from 'd3';
import { isUndefined, reduce } from 'lodash';
import React, { createContext, useCallback, useMemo, useState } from 'react';
import { useDynamicLeftAxisSpacing } from '../../hooks/useDynamicLeftAxisSpacing';
import { useOrderedDataset } from '../../hooks/useOrderedDataset';
import { Point, TrendDataSet, getValidPointValue } from '../../util';
import { ChartSpacing } from '../../util/spacing';
import { AbstractHoverZoneGrouped } from '../AbstractHoverZone';
import { DragData, TooltipCoordinateData } from '../AbstractHoverZone/types';
import { BottomAxis } from '../BottomAxis';
import { Chart } from '../Chart';
import { CHART_POINT_HIGHLIGHT_SCALE_UP } from '../ChartPoint';
import { GridLines } from '../GridLines';
import { HoverLine } from '../HoverLine';
import { LeftAxis, useLeftAxis } from '../LeftAxis';
import { getDurationInDays, getPointDate, getXTicks, xTimeFormat } from './utils';
import { useFlags } from '@fullstory/react-hooks';
import css from './TrendsChart.module.scss';
import { TrendChartLine } from './TrendChartLine';

const TOOLTIP_OFFSET = 4;

export type TrendsChartAlignment = 'start' | 'end';

// any props the callback needs can be grabbed from TrendsChartContext
export type OverlayRenderFn = () => React.ReactNode;

export type GraphTooltipProps<P extends TrendPoint> = {
  data: TooltipCoordinateData<P> | null;
  isEndDateAligned: boolean;
};

export type TrendsChartSizing = {
  // another y value to be considered when y-max is being calculated
  yMaxAdditional?: number;
  // another y value to be considered when y-min is being calculated
  yMinAdditional?: number;
};

export interface TrendPoint extends Point {
  datasetItemIndex: number;
}

export interface TrendChartDataSet<P extends TrendPoint> extends TrendDataSet {
  points: P[];
}

export type TrendsChartState = {
  dataset: TrendChartDataSet<TrendPoint>[];
  dotRadius: number;
  height: number;
  pointerPosition: { x: number; y: number } | null;
  width: number;
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  overlay?: HTMLDivElement | null;
  dragData: DragData<TrendPoint> | null;
  setDragData: (data: DragData<TrendPoint> | null) => void;
};

export const TrendsChartContext = createContext<TrendsChartState | null>(null);

export const useTrendsChartState = () => {
  const context = React.useContext(TrendsChartContext);
  if (!context) {
    throw new Error('useTrendsChartState must be used within a TrendsChartProvider');
  }
  return context;
};

export interface TrendsChartProps<P extends TrendPoint> {
  dataset: TrendChartDataSet<P>[];
  frequency: TrendFrequency;
  focus?: number;
  displayFormat: Display;
  showDots?: boolean;
  dotRadius?: number;
  preventHover?: boolean;
  chartHeight?: number;
  /** Align the domain and x-axis of the chart to the end dates of the given dataset */
  xAxisAlign?: TrendsChartAlignment;
  renderBackgroundOverlay?: OverlayRenderFn;
  renderForegroundOverlay?: OverlayRenderFn;
  sizingOptions?: TrendsChartSizing;
  /**
   * Opt-in to showing affordances for partial time periods.
   *
   * @default false
   */
  showPartialPeriods?: boolean;
  children: (params: GraphTooltipProps<P>) => React.ReactNode;
  comparisonChangeColor?: Color;
}

/*
 * TrendsChart is responsible for rendering the chart and tooltips. It should not be mapping any data.
 * It started as just for metrics but is now used in a few other places too
 */
export const TrendsChart = <P extends TrendPoint>(props: TrendsChartProps<P>) => {
  const [spacing, setSpacing] = useState({ top: 8, right: 44, bottom: 58, left: 50 });
  const flags = useFlags();
  return (
    <Chart data-component="TrendsChart" height={props.chartHeight ?? 400} spacing={spacing}>
      {({ width, height, overlay }) => (
        <InnerTrendsChart
          width={width}
          height={height}
          overlay={overlay}
          spacing={spacing}
          setSpacing={setSpacing}
          useHourlyToDayLabels={flags.featureFlags().AppHourlyUseDayLabels}
          {...props}
        />
      )}
    </Chart>
  );
};

function InnerTrendsChart<P extends TrendPoint>({
  width,
  height,
  overlay,
  dataset,
  frequency,
  displayFormat,
  spacing,
  setSpacing,
  focus,
  dotRadius = 4,
  preventHover,
  xAxisAlign = 'start',
  renderBackgroundOverlay,
  renderForegroundOverlay,
  sizingOptions,
  showPartialPeriods = false,
  useHourlyToDayLabels = false,
  children,
}: {
  width: number;
  height: number;
  overlay: HTMLDivElement | null;
  spacing: ChartSpacing;
  setSpacing: (spacing: ChartSpacing) => void;
  useHourlyToDayLabels: boolean;
} & TrendsChartProps<P>) {
  const [tooltipCoordinateData, setTooltipCoordinateData] =
    useState<TooltipCoordinateData<P> | null>(null);

  const [dragData, setDragData] = useState<DragData<P> | null>(null);

  const durationUnit = dataset[0]?.durationUnit;

  const flattened = useMemo(() => dataset.flatMap(d => d.points), [dataset]);

  const isEndDateAligned = xAxisAlign === 'end';
  const domain = useMemo(
    () => extent(flattened, d => getPointDate(d, isEndDateAligned, frequency)),
    [flattened, frequency, isEndDateAligned],
  );
  const xScale = useMemo(
    () =>
      scaleTime()
        .domain(domain[0] !== undefined ? domain : [0, 0])
        .range([0, width]),
    [domain, width],
  );
  const durationInDays = getDurationInDays(xScale);

  const maxY = Math.max(
    max(flattened, d => getValidPointValue(d?.value)) ?? 0,
    sizingOptions && sizingOptions.yMaxAdditional ? sizingOptions.yMaxAdditional : 0,
  );
  const minY = Math.min(
    min(flattened, d => getValidPointValue(d?.value)) ?? 0,
    sizingOptions && sizingOptions.yMinAdditional ? sizingOptions.yMinAdditional : 0,
  );
  const yScale = useMemo(
    () => scaleLinear().domain([minY, maxY]).range([height, 0]),
    [minY, maxY, height],
  );

  const maxLength = reduce<TrendChartDataSet<P>, number>(
    dataset,
    (prev, curr) => (curr?.points.length > prev ? curr?.points.length : prev),
    0,
  );

  // prevent more ticks than data points
  const xTicks = getXTicks(xScale, frequency, maxLength, isEndDateAligned, useHourlyToDayLabels);
  const yTicks = useMemo(() => ticks(minY, maxY, 5), [minY, maxY]);

  const currLine = (tds: TrendDataSet) =>
    line<P>()
      .x(d => xScale(getPointDate(d, isEndDateAligned, frequency)))
      .y(d => yScale(getValidPointValue(d?.value)))
      .curve(tds.curve ?? curveLinear);

  const yValues = useLeftAxis(yScale, yTicks, displayFormat, durationUnit ?? undefined);
  useDynamicLeftAxisSpacing(yValues, spacing, setSpacing);

  // render active tooltip data points on top
  const orderedValidatedDataset = useOrderedDataset(
    dataset,
    tooltipCoordinateData?.groupedPoints ?? null,
    tooltipCoordinateData?.closestPoint,
  );

  const state = useMemo(
    () => ({
      dotRadius,
      height,
      overlay,
      pointerPosition: tooltipCoordinateData,
      width,
      xScale,
      yScale,
      dataset,
      dragData,
      setDragData,
    }),
    [dotRadius, height, overlay, tooltipCoordinateData, width, xScale, yScale, dataset, dragData],
  );

  const getAxisClass = useCallback(
    (date: Date) => {
      if (!dragData || dragData?.startX == null || dragData?.endX == null) {
        return '';
      }

      const point = xScale(date);

      return point === dragData.startX || point === dragData.endX ? css.highlighted : '';
    },
    [dragData, xScale],
  );

  const getPointX = useCallback(
    (p: P) => xScale(getPointDate(p, isEndDateAligned, frequency)),
    [xScale, isEndDateAligned, frequency],
  );
  const getPointY = useCallback((p: P) => yScale(getValidPointValue(p.value)), [yScale]);

  return (
    <TrendsChartContext.Provider value={state}>
      <AbstractHoverZoneGrouped<P>
        overlay={overlay}
        dataPoints={flattened}
        dataToX={d => getPointDate(d, isEndDateAligned, frequency).getTime()}
        dataToY={d => getValidPointValue(d?.value)}
        xScale={xScale}
        getInvertedXScaleValue={x => xScale.invert(x).getTime()}
        yScale={yScale}
        tooltipCoordinateData={tooltipCoordinateData}
        setTooltipCoordinateData={setTooltipCoordinateData}
        setDragData={setDragData}
        tooltipOffset={dotRadius * CHART_POINT_HIGHLIGHT_SCALE_UP + TOOLTIP_OFFSET}
        tooltipPlacement="right"
      >
        {_ => {
          if (!tooltipCoordinateData || preventHover) return <></>;
          return children({ data: tooltipCoordinateData, isEndDateAligned });
        }}
      </AbstractHoverZoneGrouped>
      <BottomAxis<Date>
        width={width}
        height={height}
        xScale={xScale}
        xTicks={xTicks}
        spacing={{
          ...spacing,
          bottom: 24,
        }}
        rotate
        ruler
        textClass={getAxisClass}
      >
        {(xValue: Date) => xTimeFormat(xValue, frequency, durationInDays, useHourlyToDayLabels)}
      </BottomAxis>
      <LeftAxis height={height} spacing={spacing} yValues={yValues} />
      <GridLines
        width={width}
        height={height}
        xTicks={xTicks}
        yTicks={yTicks}
        xScale={xScale}
        yScale={yScale}
      />
      {tooltipCoordinateData && !preventHover && (
        <HoverLine
          x={tooltipCoordinateData.x}
          yStart={maxY ? yScale(maxY) : undefined}
          yEnd={yScale(0)}
        />
      )}
      {renderBackgroundOverlay?.()}
      {orderedValidatedDataset?.length
        ? orderedValidatedDataset.map((d, i) =>
            d && i !== focus ? (
              <TrendChartLine
                key={`${d.id}`}
                data={d}
                showPartialPeriods={showPartialPeriods}
                lineFunction={currLine}
                dotRadius={dotRadius}
                tooltipCoordinateData={tooltipCoordinateData}
                getPointX={getPointX}
                getPointY={getPointY}
                focused={isUndefined(focus)}
              />
            ) : null,
          )
        : null}
      {!isUndefined(focus) && dataset[focus] ? (
        <TrendChartLine
          key="focus-container"
          data={dataset[focus]!}
          showPartialPeriods={showPartialPeriods}
          lineFunction={currLine}
          dotRadius={dotRadius}
          getPointX={getPointX}
          getPointY={getPointY}
          focused
        />
      ) : null}
      {renderForegroundOverlay?.()}
    </TrendsChartContext.Provider>
  );
}
